import { HourSlot } from './availability-dto';

/**
 * The wire contract of `GET /leads/availability` (and `/leads/standard_availability`).
 *
 * Deliberately separate from `WeekAvailabilityDto`, which belongs to the older
 * `/donations/availability` + `/donations/courier/availability` pair. The two are not
 * the same response wearing different names:
 *
 *  - a courier week is reported here as `courier`; the legacy pair has no such field
 *  - the zone flags are OMITTED on a courier week here, where the legacy response
 *    always carries them
 *  - hourly capacity arrives as `am_slots`/`pm_slots` rather than one `slots` list
 *
 * Sharing one interface made every one of those optional and let the endpoints'
 * differences hide behind the type instead of being stated by it.
 */
export interface LeadWeekAvailabilityDto {
  week?: number;
  year?: number;
  available: LeadAvailabilityDayDto[];

  /** A courier week: days carry hourly slots and the zone flags below are absent. */
  courier?: boolean;

  in_store_zone?: boolean;
  in_vehicle_zone?: boolean;
  /**
   * The charity requires a zone this donor's zip falls outside of, so being out of
   * zone is final rather than a warning. Absent on a courier week — always test it
   * as `=== true`, never as the truthiness of its negation.
   */
  in_zone_required?: boolean;

  /** `/leads/standard_availability` only. */
  in_charity_vehicle_zone?: boolean;
  /** `/leads/standard_availability` only — the earliest free charity date. */
  charity_first_available?: string | null;
}

/**
 * One day. The endpoint returns EITHER a daily day (`am`/`pm`) or a courier day
 * (`am_slots`/`pm_slots`) — never both, and a courier day carries no `am`/`pm` at
 * all. `LeadAvailabilityComponent.normalizeDays` is the one place that folds the
 * two into the single shape the calendar renders.
 */
export interface LeadAvailabilityDayDto {
  date: string;
  am?: boolean;
  pm?: boolean;
  am_slots?: HourSlot[];
  pm_slots?: HourSlot[];
  /** Which side the day's capacity came from (`/leads/standard_availability` only). */
  source?: 'charity' | 'partner';
}
